<template lang="pug">
div(style='display: flex; align-items: center; gap: 7px; flex-shrink: 0; position: relative;')
    span(style='font-size: 0.65rem; font-weight: 700; letter-spacing: 0.08em; color: rgba(255,255,255,0.4); text-transform: uppercase;') ALERTS
    button(
        type='button'
        @click='togglePanel'
        :style='badgeStyle'
        style='border: none; padding: 2px 8px; border-radius: 10px; cursor: pointer; font-size: 0.72rem; font-weight: 600; line-height: 1.2;'
    )
        span(v-if='unacknowledgedCount === 0' style='color: rgba(255,255,255,0.55);') idle
        span(v-else style='color: white;') {{ unacknowledgedCount }} new
    //- Popover panel — absolute-positioned; the status bar is the last
    //- thing in the viewport so we anchor above it (bottom: 100%).
    div(
        v-if='panelOpen'
        style='position: absolute; bottom: 100%; right: 0; margin-bottom: 8px; width: 380px; max-height: 420px; overflow-y: auto; background: #1f2937; border: 1px solid rgba(255,255,255,0.12); border-radius: 6px; box-shadow: 0 6px 24px rgba(0,0,0,0.4); z-index: 100;'
    )
        div(style='display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; border-bottom: 1px solid rgba(255,255,255,0.08);')
            span(style='font-size: 0.85rem; font-weight: 600; color: rgba(255,255,255,0.9);') Recent alerts
            button(
                type='button'
                @click='closePanel'
                style='background: transparent; border: none; color: rgba(255,255,255,0.4); cursor: pointer; font-size: 1.1rem; line-height: 1;'
            ) ×
        div(v-if='recentAlerts.length === 0' style='padding: 24px 14px; text-align: center; color: rgba(255,255,255,0.4); font-size: 0.8rem;')
            | No alerts yet. Save a plan with an
            code(style='color: rgba(255,255,255,0.55); background: rgba(255,255,255,0.06); padding: 0 4px; border-radius: 3px; margin: 0 2px;') alert
            | block to arm the evaluator.
        div(v-else)
            div(
                v-for='alert in recentAlerts'
                :key='alert.id'
                :style='rowStyle(alert)'
                style='display: flex; gap: 10px; padding: 10px 14px; border-bottom: 1px solid rgba(255,255,255,0.04); font-size: 0.78rem;'
            )
                div(:style='severityBar(alert.severity)' style='width: 3px; align-self: stretch; border-radius: 2px;')
                div(style='flex: 1; min-width: 0;')
                    div(style='display: flex; justify-content: space-between; gap: 8px;')
                        span(style='color: white; font-weight: 600;') {{ alert.alertName }}
                        span(style='color: rgba(255,255,255,0.4); font-size: 0.7rem; white-space: nowrap;') {{ formatRelative(alert.firedAtUtc) }}
                    div(style='color: rgba(255,255,255,0.65); margin-top: 2px;')
                        | {{ alert.triggerKind.toUpperCase() }}
                        template(v-if='alert.subjectAssetName')
                            |  · subject
                            span(style='color: white; margin-left: 3px;') {{ alert.subjectAssetName }}
                    div(v-if='alert.cpaMeters !== null || alert.tcpaSeconds !== null' style='color: rgba(255,255,255,0.45); margin-top: 2px; font-size: 0.72rem;')
                        template(v-if='alert.cpaMeters !== null')
                            | CPA {{ formatMeters(alert.cpaMeters) }}
                        template(v-if='alert.cpaMeters !== null && alert.tcpaSeconds !== null')
                            |  ·
                        template(v-if='alert.tcpaSeconds !== null')
                            |  TCPA {{ formatSeconds(alert.tcpaSeconds) }}
                    div(v-if='alert.description' style='color: rgba(255,255,255,0.5); margin-top: 3px; font-style: italic;') {{ alert.description }}
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import { useAlertsStore, type AlertLogEntry } from '@/store/storeAlerts';

const store = useAlertsStore();
const { recentAlerts, unacknowledgedCount } = storeToRefs(store);
const panelOpen = ref(false);

onMounted(() => store.startPolling());
onBeforeUnmount(() => store.stopPolling());

function togglePanel() {
    panelOpen.value = !panelOpen.value;
    if (panelOpen.value) store.acknowledgeAll();
}

function closePanel() {
    panelOpen.value = false;
}

const badgeStyle = computed(() => {
    if (unacknowledgedCount.value > 0) {
        return { background: '#dc2626' };
    }
    return { background: 'rgba(255,255,255,0.08)' };
});

function severityBar(severity: string | null) {
    switch (severity) {
        case 'high':   return { background: '#dc2626' };
        case 'medium': return { background: '#f59e0b' };
        case 'low':    return { background: '#60a5fa' };
        default:       return { background: 'rgba(255,255,255,0.15)' };
    }
}

function rowStyle(_alert: AlertLogEntry) {
    return {};
}

function formatMeters(m: number): string {
    if (m < 1000) return `${Math.round(m)} m`;
    return `${(m / 1000).toFixed(2)} km`;
}

function formatSeconds(s: number): string {
    if (s < 60) return `${Math.round(s)} s`;
    if (s < 3600) return `${(s / 60).toFixed(1)} min`;
    return `${(s / 3600).toFixed(1)} h`;
}

function formatRelative(isoUtc: string): string {
    const then = new Date(isoUtc).getTime();
    const delta = Date.now() - then;
    if (delta < 60_000) return 'just now';
    if (delta < 3_600_000) return `${Math.round(delta / 60_000)} min ago`;
    if (delta < 86_400_000) return `${Math.round(delta / 3_600_000)} h ago`;
    return new Date(isoUtc).toLocaleDateString();
}
</script>
